// components/Auth/LoginPanel.jsx
'use client'
import { useState } from 'react'
import { signIn } from 'next-auth/react'

export const LOGIN_LABELS = {
  en: {
    title: 'Log in or sign up',
    google: 'Continue with Google',
    or: 'or',
    emailLabel: 'Email',
    placeholder: 'you@example.com',
    sendLink: 'Email me a login link',
    sending: 'Sending…',
    sentTitle: 'Check your email',
    sentBody: (email) => `We sent a login link to ${email}. It expires in 15 minutes.`,
    differentEmail: 'Use a different email',
    invalid_email: 'Enter a valid email address.',
    rate_limited: 'Too many requests. Wait a minute and try again.',
    send_failed: "The email couldn't be sent. Try again in a moment.",
  },
  th: {
    title: 'เข้าสู่ระบบหรือสมัครสมาชิก',
    google: 'ดำเนินการต่อด้วย Google',
    or: 'หรือ',
    emailLabel: 'อีเมล',
    placeholder: 'you@example.com',
    sendLink: 'ส่งลิงก์เข้าสู่ระบบทางอีเมล',
    sending: 'กำลังส่ง…',
    sentTitle: 'ตรวจสอบอีเมลของคุณ',
    sentBody: (email) => `เราได้ส่งลิงก์เข้าสู่ระบบไปที่ ${email} แล้ว ลิงก์จะหมดอายุใน 15 นาที`,
    differentEmail: 'ใช้อีเมลอื่น',
    invalid_email: 'กรุณากรอกอีเมลให้ถูกต้อง',
    rate_limited: 'ส่งคำขอบ่อยเกินไป กรุณารอสักครู่แล้วลองอีกครั้ง',
    send_failed: 'ไม่สามารถส่งอีเมลได้ กรุณาลองอีกครั้ง',
  },
}

const inputStyle = {
  width: '100%', boxSizing: 'border-box', padding: '11px 12px', fontSize: '0.9375rem',
  border: '1.5px solid #d1d5db', borderRadius: 8, outline: 'none', color: '#111827', background: '#fff',
}

/**
 * Google + magic-link login form. Used by the login modal, the /signin page
 * and the expired-link state of /auth/verify.
 */
export default function LoginPanel({
  lang = 'en', callbackUrl, accentColor = 'var(--color-main)',
  providers = ['google', 'email'], showTitle = true,
}) {
  const t = { ...LOGIN_LABELS.en, ...LOGIN_LABELS[lang] }
  const [email, setEmail]   = useState('')
  const [status, setStatus] = useState('idle') // idle | sending | sent
  const [error, setError]   = useState(null)

  const returnTo = () => callbackUrl ?? window.location.href

  const sendLink = async (e) => {
    e.preventDefault()
    setError(null)
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) { setError('invalid_email'); return }
    setStatus('sending')
    try {
      const res  = await fetch('/api/magic-link', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), callbackUrl: returnTo(), lang }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) { setError(data.error ?? 'send_failed'); setStatus('idle'); return }
      setStatus('sent')
    } catch {
      setError('send_failed')
      setStatus('idle')
    }
  }

  if (status === 'sent') {
    return (
      <div>
        <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#111827', margin: '0 0 8px' }}>{t.sentTitle}</h2>
        <p style={{ fontSize: '0.9375rem', color: '#4b5563', lineHeight: 1.5, margin: '0 0 20px' }}>{t.sentBody(email.trim())}</p>
        <button onClick={() => { setStatus('idle'); setEmail('') }}
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: accentColor, fontSize: '0.875rem', fontWeight: 600 }}>
          {t.differentEmail}
        </button>
      </div>
    )
  }

  return (
    <div>
      {showTitle && <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#111827', margin: '0 0 20px' }}>{t.title}</h2>}

      {providers.includes('google') && (
        <button onClick={() => signIn('google', { callbackUrl: returnTo() })}
          style={{
            width: '100%', padding: '11px 12px', fontSize: '0.9375rem', fontWeight: 600,
            color: '#111827', background: '#fff', border: '1.5px solid #d1d5db', borderRadius: 8, cursor: 'pointer',
          }}>
          {t.google}
        </button>
      )}

      {providers.includes('google') && providers.includes('email') && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '18px 0', color: '#9ca3af', fontSize: '0.8125rem' }}>
          <span style={{ flex: 1, height: 1, background: '#e5e7eb' }} />
          {t.or}
          <span style={{ flex: 1, height: 1, background: '#e5e7eb' }} />
        </div>
      )}

      {providers.includes('email') && (
        <form onSubmit={sendLink} noValidate>
          <label htmlFor="login-email" style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 600, color: '#374151', marginBottom: 6 }}>
            {t.emailLabel}
          </label>
          <input id="login-email" type="email" autoComplete="email" inputMode="email"
            value={email} onChange={e => setEmail(e.target.value)} placeholder={t.placeholder}
            aria-invalid={!!error} aria-describedby={error ? 'login-email-error' : undefined}
            style={{ ...inputStyle, borderColor: error ? '#dc2626' : '#d1d5db' }} />
          {error && (
            <p id="login-email-error" role="alert" style={{ color: '#dc2626', fontSize: '0.8125rem', margin: '6px 0 0' }}>
              {t[error] ?? t.send_failed}
            </p>
          )}
          <button type="submit" disabled={status === 'sending'}
            style={{
              width: '100%', marginTop: 12, padding: '11px 12px', fontSize: '0.9375rem', fontWeight: 600,
              color: '#fff', background: accentColor, border: 'none', borderRadius: 8,
              cursor: status === 'sending' ? 'default' : 'pointer', opacity: status === 'sending' ? 0.7 : 1,
            }}>
            {status === 'sending' ? t.sending : t.sendLink}
          </button>
        </form>
      )}
    </div>
  )
}